/**
 * Dedicated alerts inbox: filters by severity/type, deep link to resolution.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import { PageHeader, Badge, Button } from '../ui';
import { BrandLoading } from '../Brand';
import '../../App.css';

function formatNotificationTime(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function notificationBadgeVariant(severity) {
  if (severity === 'critical') return 'danger';
  if (severity === 'warning') return 'warning';
  if (severity === 'positive') return 'success';
  return 'info';
}

function notificationFilterSeverity(severity) {
  if (severity === 'critical') return 'high';
  if (severity === 'warning') return 'med';
  if (severity === 'positive') return 'low';
  return null;
}

function applyFeedResponse(res) {
  const payload = res?.data;
  return Array.isArray(payload?.notifications) ? payload.notifications : [];
}

export default function AlertsPage() {
  const navigate = useNavigate();
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [auditMissing, setAuditMissing] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all'); // all | high | med | low

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API_URL}/api/compliance/timeline`, { params: { range: 90 }, ...auth }).then((r) => r.data?.events || []).catch(() => []),
      axios.get(`${API_URL}/api/audit/readiness`, auth).then((r) => r.data?.missingItems || []).catch(() => []),
      axios.get(`${API_URL}/api/notifications`, auth).then(applyFeedResponse).catch(() => []),
    ]).then(([events, missing, feed]) => {
      setTimelineEvents(events || []);
      setAuditMissing(missing || []);
      setNotifications(feed || []);
    }).finally(() => setLoading(false));
  }, []);

  const alerts = useMemo(() => {
    const fromMissing = (auditMissing || []).map((m) => ({
      id: `m-${m.item}`,
      type: 'missing',
      title: m.item,
      detail: m.detail,
      severity: 'high',
      actionUrl: '/audit',
      date: null,
    }));
    const fromTimeline = (timelineEvents || []).map((e) => ({
      id: e.id,
      type: 'timeline',
      title: e.title,
      detail: null,
      severity: e.severity || 'med',
      actionUrl: e.actionUrl,
      date: e.date,
    }));
    const combined = [...fromMissing, ...fromTimeline];
    if (severityFilter === 'all') return combined;
    return combined.filter((a) => a.severity === severityFilter);
  }, [auditMissing, timelineEvents, severityFilter]);

  const visibleNotifications = useMemo(() => {
    const list = notifications || [];
    if (severityFilter === 'all') return list;
    return list.filter((n) => notificationFilterSeverity(n.severity) === severityFilter);
  }, [notifications, severityFilter]);

  const hasUnread = (notifications || []).some((n) => !n.isRead);

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

  const markAllRead = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.patch(`${API_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await axios.get(`${API_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(applyFeedResponse(res));
    } catch {
      /* ignore */
    }
  };

  const onNotificationClick = async (n) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.patch(`${API_URL}/api/notifications/${n.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, isRead: true } : item)));
      } catch {
        /* still navigate when the route is valid */
      }
    }
    if (n.route && n.route.startsWith('/')) {
      navigate(n.route);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="dashboard-loading">
          <BrandLoading message="Loading alerts..." />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Alerts"
        subtitle="Notifications, compliance deadlines, and missing items."
        secondaryAction={{ label: 'Refresh', onClick: () => window.location.reload() }}
      />
      <div className="alerts-toolbar">
        <span className="text-small text-muted">Severity:</span>
        {['all', 'high', 'med', 'low'].map((s) => (
          <button
            key={s}
            type="button"
            className={`ui-tabs__tab ${severityFilter === s ? 'ui-tabs__tab--active' : ''}`}
            onClick={() => setSeverityFilter(s)}
          >
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>
      <div className="dashboard-dense-section" style={{ marginTop: 0 }}>
        <h3>Recent notifications</h3>
        {hasUnread && (
          <div style={{ marginBottom: 8 }}>
            <Button variant="ghost" size="sm" onClick={markAllRead}>
              Mark all as read
            </Button>
          </div>
        )}
        <ul className="dashboard-dense-list">
          {visibleNotifications.length === 0 ? (
            <li>No notifications</li>
          ) : (
            visibleNotifications.map((n) => (
              <li
                key={n.id}
                role="button"
                tabIndex={0}
                className="alerts-notification-row"
                aria-label={`${n.title}. ${n.isRead ? 'read' : 'unread'}`}
                onClick={() => onNotificationClick(n)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (e.key === ' ') e.preventDefault();
                    onNotificationClick(n);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <span>
                  <Badge variant={notificationBadgeVariant(n.severity)} style={{ marginRight: 8 }}>
                    {n.severity}
                  </Badge>
                  {n.title}
                  {n.subtitle && ` — ${n.subtitle}`}
                  {!n.isRead && (
                    <span
                      aria-label="Unread"
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: 'var(--amber, #e09b1a)',
                        marginLeft: 8,
                        verticalAlign: 'middle',
                      }}
                    />
                  )}
                </span>
                <span className="text-small text-muted">{formatNotificationTime(n.createdAt)}</span>
              </li>
            ))
          )}
        </ul>
      </div>
      <div className="dashboard-dense-section" style={{ marginTop: 24 }}>
        <h3>Deadlines & missing items</h3>
        <ul className="dashboard-dense-list">
          {alerts.length === 0 ? (
            <li>No alerts</li>
          ) : (
            alerts.map((a) => (
              <li key={a.id}>
                <span>
                  <Badge variant={a.severity === 'high' ? 'danger' : a.severity === 'med' ? 'warning' : 'info'} style={{ marginRight: 8 }}>
                    {a.severity}
                  </Badge>
                  {a.title}
                  {a.detail && ` — ${a.detail}`}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {a.date && <span className="text-small text-muted">{formatDate(a.date)}</span>}
                  {a.actionUrl && (
                    <Button variant="ghost" size="sm" onClick={() => navigate(a.actionUrl)}>
                      Resolve
                    </Button>
                  )}
                </span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
